import React, { useState, ChangeEvent, useEffect } from 'react';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import MarkdownRenderer from '../MarkdownRenderer/MarkdownRenderer';

import './styles.css';

export interface IngredientBasedAnswerContext {
    defaultValueForUnanswered: string;
    answerToLocalizedContextMessageMap: { [nativeAnswer: string]: string };
    suggestedAnswer: string | null;
    hasNonCompliantIngredients: boolean;
    onlyShowWhenAnswerConflictsWithSuggestion?: boolean;
}

export interface PlausibilityDataAnswerContext {
    suggestedAnswer: string | null;
    defaultValueForUnanswered: string;
    answerToLocalizedContextMessageMap: { [nativeAnswer: string]: string };
}

interface RadioButtonsProps {
    options: string[];
    value: string;
    valueComment: string;
    ingredientBasedAnswerContext?: IngredientBasedAnswerContext | null;
    plausibilityBasedAnswerContext: PlausibilityDataAnswerContext | null;
    onValueChange?: (value: string, valueComment: string) => void;
    getFriendlyNameForOptions: (value: string) => string;
    isDisabled?: boolean;
    localizer: IStringLocalizer;
    questionAlertHeader: JSX.Element | undefined;
}

const RadioButtons: React.FC<RadioButtonsProps> = ({ value, valueComment, ingredientBasedAnswerContext, plausibilityBasedAnswerContext, options, onValueChange, getFriendlyNameForOptions, isDisabled, localizer, questionAlertHeader }) => {
    const [selectedOptionValue, setSelectedOptionValue] = useState(value);
    const [valueCommentToUse, setValueCommentToUse] = useState(valueComment);

    useEffect(() => {
        setSelectedOptionValue(value);
    }, [value]);

    useEffect(() => {
        setValueCommentToUse(valueComment);
    }, [valueComment]);

    useEffect(() => {
        if (!ingredientBasedAnswerContext) return;
        if (selectedOptionValue.length === 0) {
            if (ingredientBasedAnswerContext.hasNonCompliantIngredients) {
                setSelectedOptionValue(ingredientBasedAnswerContext.defaultValueForUnanswered);

                if (onValueChange) {
                    onValueChange(ingredientBasedAnswerContext.defaultValueForUnanswered, valueCommentToUse);
                }
            }
        }
    }, [ingredientBasedAnswerContext]);

    useEffect(() => {
        if (plausibilityBasedAnswerContext === null) return;
        if (selectedOptionValue.length === 0) {
            setSelectedOptionValue(plausibilityBasedAnswerContext.defaultValueForUnanswered);

            if (onValueChange) {
                onValueChange(plausibilityBasedAnswerContext.defaultValueForUnanswered, valueCommentToUse);
            }
        }
    }, []);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setSelectedOptionValue(newValue);

        if (onValueChange) {
            onValueChange(newValue, valueCommentToUse);
        }
    };

    const valueComment_change = (newValue: string) => {
        setValueCommentToUse(newValue);

        if (onValueChange) {
            onValueChange(selectedOptionValue, newValue);
        }
    }

    const selectedValue = selectedOptionValue ?? value ?? "";
    const ingredientValueToCompare = ingredientBasedAnswerContext?.onlyShowWhenAnswerConflictsWithSuggestion ? selectedValue : value;
    const suggestedAnswer = ingredientBasedAnswerContext?.suggestedAnswer ?? null;
    const showValueComment = (suggestedAnswer !== null) && (ingredientValueToCompare !== "" && ingredientValueToCompare.toLowerCase() !== suggestedAnswer.toLowerCase());
    const showIngredientSoftCheck = !ingredientBasedAnswerContext?.onlyShowWhenAnswerConflictsWithSuggestion || showValueComment;

    const suggestedAnswerPlausibility = (plausibilityBasedAnswerContext && plausibilityBasedAnswerContext.suggestedAnswer !== null && plausibilityBasedAnswerContext.suggestedAnswer !== "") ? plausibilityBasedAnswerContext.suggestedAnswer : null;
    const showPlausibilityValueComment = (suggestedAnswerPlausibility !== null) && (value !== "" && value.toLowerCase() !== suggestedAnswerPlausibility.toLowerCase());

    return (
        <div className="radio-options-container">
            {questionAlertHeader && questionAlertHeader}
            {options.map((optionValue, i) => {
                const tag = ingredientBasedAnswerContext?.answerToLocalizedContextMessageMap[optionValue.toLowerCase()] ?? null;
                const plausibilityTag = plausibilityBasedAnswerContext?.answerToLocalizedContextMessageMap[optionValue] ?? null;
                return (
                    <div key={i} className="radio">
                        <label>
                            <input disabled={isDisabled} type="radio" value={optionValue} onChange={handleInputChange} checked={selectedOptionValue?.toLowerCase() === optionValue?.toLowerCase()} />
                            {" "}
                            <MarkdownRenderer htmlText={getFriendlyNameForOptions(optionValue).replace('<', '\\<').replace('>', '\\>')} />
                            {tag && showIngredientSoftCheck &&
                                <span className="option-suggestion">{tag}</span>
                            }
                            {plausibilityTag &&
                                <span className="option-suggestion">{plausibilityTag}</span>
                            }
                        </label>
                    </div>
                );
            })}

            {showValueComment &&
                <div className="value-comment">
                    <div className="message">{localizer.localizeString("Since your answer differs from the suggested answer, please leave a comment explaining.")}</div>
                    <textarea className="form-control" onChange={e => valueComment_change(e.target.value)} value={valueCommentToUse}>
                    </textarea>
                </div>
            }
            {
                showPlausibilityValueComment &&
                <div className="value-comment">
                    <div className="message">{localizer.localizeString("Since your answer differs from the suggested answer, please leave a comment explaining.")}</div>
                    <textarea className="form-control" onChange={e => valueComment_change(e.target.value)} value={valueCommentToUse}>
                    </textarea>
                </div>
            }
        </div>
    );
}

export default RadioButtons;
